import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SESSION_ABSOLUTE_MAX_AGE_SEC,
  SESSION_IDLE_MAX_AGE_SEC,
} from "@lms/backend/lib/auth/session-policy";
import { logoutSession } from "@/services/api/auth";
import { postSessionHeartbeat } from "@/services/api/tenants";
import { useAuthStore } from "@/stores/authStore";
import { forgetPendingInvite } from "@/lib/pending-invite";
import {
  broadcastSignOut,
  clearSessionActivity,
  onOtherTabSignOut,
  readLastActivity,
  signInPath,
  touchSessionActivity,
  type SignOutReason,
} from "@/lib/session-activity";

/**
 * N16: 8 hours — used only when the server answered without a usable limit. H1b: until the server
 * has answered, the browser neither warns nor signs out (production runs 7 days, set by env).
 */
const DEFAULT_IDLE_MS = SESSION_IDLE_MAX_AGE_SEC * 1000;
/** A server value below this (or not a number) is ignored — it would sign everyone out. */
const MIN_IDLE_MS = 5 * 60_000;
/** "Still there?" appears this long before the idle sign-out (AUTH-1). */
export const IDLE_WARNING_MS = 60_000;
const TICK_MS = 1_000;
const HEARTBEAT_EVERY_MS = 60_000;
/**
 * F4: after this many failed asks for the limit (about this many minutes), the guard stops waiting
 * for the server and enforces the last limit the server reported in this browser, or else the
 * longest one possible — the 30-day session lifetime. Never early, but always eventually.
 */
const FAILED_ASKS_BEFORE_FALLBACK = 3;
const FALLBACK_IDLE_MS = SESSION_ABSOLUTE_MAX_AGE_SEC * 1000;
/** F4: the last limit a heartbeat reported, kept across page loads. */
export const IDLE_LIMIT_KEY = "lms_spa_idle_limit_ms";

function readStoredIdleLimit(): number | null {
  try {
    const value = Number.parseInt(window.localStorage.getItem(IDLE_LIMIT_KEY) ?? "", 10);
    return Number.isFinite(value) && value >= MIN_IDLE_MS ? value : null;
  } catch {
    return null;
  }
}

function storeIdleLimit(ms: number): void {
  try {
    window.localStorage.setItem(IDLE_LIMIT_KEY, String(ms));
  } catch {
    // Storage full or blocked: the 30-day fallback still applies.
  }
}

/**
 * SPA-4: the server's idle limit, once a heartbeat has reported it. Module state, not a local of
 * the idle effect: that effect re-runs on route changes, and the limit must outlive them. Fed by
 * every heartbeat (this hook's and TenantLayout's).
 */
let serverIdleMs: number | null = null;
/** H1b: the server has answered a heartbeat (with or without a usable limit). */
let serverAnswered = false;
/** F4: heartbeats that failed before the server ever answered. */
let failedAsks = 0;

/** Records `idleTimeoutSec` from a heartbeat answer; nonsense values are ignored. */
export function rememberServerIdleLimit(policy: { idleTimeoutSec?: number } | null): void {
  serverAnswered = true;
  const sec = policy?.idleTimeoutSec;
  if (typeof sec === "number" && Number.isFinite(sec) && sec * 1000 >= MIN_IDLE_MS) {
    serverIdleMs = sec * 1000;
    storeIdleLimit(serverIdleMs);
  }
}

/** F4: a heartbeat failed; counts only while the server has not answered yet. */
function noteFailedHeartbeat(): void {
  if (!serverAnswered) failedAsks += 1;
}

/**
 * The idle limit to enforce, or null while the server has not answered yet. H1b: the compiled-in
 * default must never sign anyone out on page load — a person reopening the app two days into a
 * 7-day limit would be signed out by the browser alone. The server stays the authority: it refuses
 * an idle session on the first request whatever the browser thinks. F4: if the heartbeat keeps
 * failing, the browser falls back to the last limit it heard, or the 30-day lifetime.
 */
function currentIdleMs(): number | null {
  if (serverAnswered) return serverIdleMs ?? DEFAULT_IDLE_MS;
  if (failedAsks >= FAILED_ASKS_BEFORE_FALLBACK) return readStoredIdleLimit() ?? FALLBACK_IDLE_MS;
  return null;
}

/** Tests only: module state outlives a test. */
export function resetServerIdleLimitForTests(): void {
  serverIdleMs = null;
  serverAnswered = false;
  failedAsks = 0;
}

/** Input that counts as activity. pointermove and wheel cover reading and watching (AUTH-1). */
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "pointermove",
  "wheel",
] as const;

export type SessionIdleState = {
  /** True during the last minute before an idle sign-out. */
  warningVisible: boolean;
  /** Whole seconds until the sign-out while the warning shows. */
  secondsLeft: number;
  /** "Stay signed in": counts as activity and hides the warning. */
  staySignedIn: () => void;
};

/**
 * Idle sign-out (the server's limit — 7 days in production, H1b; 8 hours by default, N16) shared
 * by every tab
 * (AUTH-1, AUTH-4, AUTH-8 client half).
 *
 * - Activity anywhere in the app — input events in any tab, or `touchSessionActivity()` from
 *   playback/uploads — resets one shared clock in localStorage.
 * - A minute before the limit the guard shows "Still there?".
 * - A heartbeat goes to the server once a minute, but only if there was activity since the last
 *   one, so the server's idle limit tracks real use and a forgotten tab expires there too.
 * - A sign-out in one tab sends every other tab to the login page, with `?next=` (AUTH-11).
 */
export function useSessionIdleTimeout(): SessionIdleState {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const clearSession = useAuthStore((s) => s.clearSession);
  const navigate = useNavigate();
  const location = useLocation();
  const [warningVisible, setWarningVisible] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const tenantSlug = user?.tenantSlug ?? null;
  // F6: /auth/post-login is a sign-in in progress (Google). A session left in this browser from
  // earlier must not be idle-checked there: signing it out would call logout and race the new
  // session's setSession. The new session starts its own clock when the page stores it.
  const signingIn = location.pathname === "/auth/post-login";
  // MT-1 AUTH-009 (§3.9): the platform console has its own clock (`usePlatformIdleTimeout`); a
  // school session left in this browser is not idle-checked or signed out there.
  const onPlatform =
    location.pathname === "/platform" || location.pathname.startsWith("/platform/");
  const currentPath = `${location.pathname}${location.search ?? ""}`;
  const pathRef = useRef(currentPath);
  pathRef.current = currentPath;
  // SPA-4: BrowserRouter's `navigate` changes on every pathname change; read it through a ref so
  // a route change doesn't restart the idle effect (and its heartbeat clock).
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const staySignedIn = useCallback(() => {
    touchSessionActivity(Date.now(), { force: true });
    setWarningVisible(false);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !tenantSlug || signingIn || onPlatform) return;
    let signingOut = false;

    const goToLogin = (reason: SignOutReason) => {
      navigateRef.current(
        signInPath(tenantSlug, pathRef.current, reason === "idle" ? "idle" : undefined),
        { replace: true },
      );
    };

    const signOutHere = () => {
      if (signingOut) return;
      signingOut = true;
      setWarningVisible(false);
      void logoutSession()
        .catch(() => undefined)
        .finally(() => {
          // Tell the other tabs why before the shared session is cleared (they see both).
          broadcastSignOut("idle");
          clearSession();
          clearSessionActivity();
          forgetPendingInvite();
          goToLogin("idle");
        });
    };

    const initial = readLastActivity();
    if (initial == null) touchSessionActivity();
    let lastHeartbeat = Date.now();
    const heartbeat = () =>
      postSessionHeartbeat().then(rememberServerIdleLimit).catch(noteFailedHeartbeat);
    // H1b: while the limit is unknown, ask for it (at most once a minute if the network fails).
    let lastLimitAsk = Number.NEGATIVE_INFINITY;
    const askForLimit = (now: number) => {
      if (now - lastLimitAsk < HEARTBEAT_EVERY_MS) return;
      lastLimitAsk = now;
      lastHeartbeat = now;
      void heartbeat();
    };

    const onActivity = () => touchSessionActivity();
    for (const event of ACTIVITY_EVENTS) {
      window.addEventListener(event, onActivity, { passive: true });
    }

    const stopListening = onOtherTabSignOut((reason) => {
      if (signingOut) return;
      signingOut = true;
      setWarningVisible(false);
      clearSession();
      forgetPendingInvite();
      goToLogin(reason);
    });

    const check = () => {
      const now = Date.now();
      const last = readLastActivity() ?? 0;
      const idleFor = now - last;
      const idleMs = currentIdleMs();
      if (idleMs === null) {
        askForLimit(now);
        return;
      }
      // F4: on a fallback limit, keep asking so the server's own limit takes over once it answers.
      if (!serverAnswered) askForLimit(now);

      if (idleFor > idleMs) {
        signOutHere();
        return;
      }

      const remaining = idleMs - idleFor;
      if (remaining <= IDLE_WARNING_MS) {
        setWarningVisible(true);
        setSecondsLeft(Math.max(1, Math.ceil(remaining / 1000)));
      } else {
        setWarningVisible(false);
      }

      if (now - lastHeartbeat >= HEARTBEAT_EVERY_MS) {
        if (last >= lastHeartbeat) {
          void heartbeat();
        }
        lastHeartbeat = now;
      }
    };

    check();
    const interval = window.setInterval(check, TICK_MS);

    return () => {
      for (const event of ACTIVITY_EVENTS) {
        window.removeEventListener(event, onActivity);
      }
      stopListening();
      window.clearInterval(interval);
    };
  }, [clearSession, isAuthenticated, tenantSlug, signingIn, onPlatform]);

  return { warningVisible, secondsLeft, staySignedIn };
}
